import {
  AfterViewInit,
  Component,
  OnChanges,
  Injector,
  Input,
  OnDestroy,
  DestroyRef,
  SimpleChanges,
  ViewChild,
  Output,
  EventEmitter,
  HostListener,
  inject,
  signal,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { UserStore } from '../../../user/store/user.store';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatLabel } from '@angular/material/form-field';
import { MatError } from '@angular/material/form-field';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatTabsModule } from '@angular/material/tabs';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatChipsModule } from '@angular/material/chips';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatIcon } from '@angular/material/icon';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { CustomersService } from '../../services/customers.service';
import { CustomerModel } from '../../customer.model';
import { ActionsService } from '../../services/actions.service';
import { CustomerSettingsFormComponent } from '../customer-settings-form/customer-settings-form.component';
import { splitEmails, joinEmails, EMAIL_PATTERN } from '../../../../shared/utils/email.util';
import { openUnsavedChangesDialog } from '../../../../shared/components/unsaved-changes-dialog/unsaved-changes-dialog.component';

export type CustomerDetailsFormType = FormGroup<{
  contactFirstName: FormControl<string | null>;
  contactLastName: FormControl<string | null>;
  displayName: FormControl<string | null>;
  companyName: FormControl<string | null>;
  companyAccountNumber: FormControl<string | null>;
  businessPhone: FormControl<string | null>;
  businessPhoneExt: FormControl<string | null>;
  mobilePhoneNumber: FormControl<string | null>;
  contactEmail: FormControl<string | null>;
  notes: FormControl<string | null>;
  customerAddress: FormControl<string | null>;
  customerCity: FormControl<string | null>;
  customerState: FormControl<string | null>;
  customerZip: FormControl<string | null>;
  parentCustomerId: FormControl<string | null>;
  salesPersonId: FormControl<string | null>;
  paymentTerms: FormControl<string | null>;
  isTestCustomer: FormControl<boolean | null>;
}>;

@Component({
  selector: 'customer-editor',
  standalone: true,
  imports: [
    MatButtonModule,
    MatFormFieldModule,
    CommonModule,
    FormsModule,
    MatInputModule,
    MatLabel,
    MatError,
    ReactiveFormsModule,
    MatButtonToggleModule,
    MatTabsModule,
    MatSelectModule,
    MatOptionModule,
    MatChipsModule,
    MatSlideToggleModule,
    MatIcon,
    NgxMaskDirective,
    CustomerSettingsFormComponent,
  ],
  providers: [provideNgxMask()],
  templateUrl: './customer-editor.component.html',
  styles: [
    `
      .email-input-row { display: flex; align-items: flex-start; gap: 0.5rem; }
      .email-input-row .email-input-field { flex: 1; }
      .email-input-row .add-email-btn { margin-top: 0.35rem; white-space: nowrap; }
      .email-error { color: #c62828; font-size: 0.85rem; }
      .email-chip-list { display: flex; flex-wrap: wrap; gap: 0.5rem; margin-top: 0.25rem; }
      .toggle-item { margin: 0.75rem 0; }
      .toggle-description { margin: 0.25rem 0 0; font-size: 0.85rem; color: rgba(0, 0, 0, 0.6); }
      /* Read-only ERP reference values. A definition list rather than a table: the rows are
         label/value pairs whose set differs per ERP, so there are no columns to align. */
      .erp-fields__hint { margin: 0 0 1rem; font-size: 0.85rem; color: rgba(0, 0, 0, 0.6); }
      .erp-fields { margin: 0; }
      .erp-fields__row {
        display: flex;
        gap: 1rem;
        padding: 0.5rem 0;
        border-bottom: 1px solid rgba(0, 0, 0, 0.08);
      }
      .erp-fields__row:last-child { border-bottom: none; }
      .erp-fields__label {
        flex: 0 0 40%;
        max-width: 40%;
        margin: 0;
        font-weight: 600;
        color: rgba(0, 0, 0, 0.7);
      }
      .erp-fields__value {
        flex: 1;
        margin: 0;
        /* ERP values are free text and can be long (a whole ship-to address) or unbroken. */
        overflow-wrap: anywhere;
        white-space: pre-wrap;
      }
      @media (max-width: 600px) {
        .erp-fields__row { flex-direction: column; gap: 0.15rem; }
        .erp-fields__label { flex-basis: auto; max-width: none; }
      }
    `,
  ],
  host: {
    class: 'app-customer-editor',
  },
})
export class CustomerEditorComponent implements AfterViewInit, OnDestroy, OnChanges {
  @Input() customerData?: CustomerModel;
  @Input() customerId?: string;
  @Input() hasDefaultPaymentMethod: boolean = false;

  /**
   * The ERP reference values to show, in label order.
   *
   * Sorted here because the map arrives as JSONB, which does not preserve key order — the
   * importer's declared order is lost in the database, so the only stable presentation is
   * alphabetical. Blank values never reach Freely (the importer omits them), so anything present
   * is worth a row.
   */
  erpFields(): { label: string; value: string }[] {
    const extras = this.customerData?.extraFields;
    if (!extras) return [];
    return Object.keys(extras)
      .sort((a, b) => a.localeCompare(b))
      .map((label) => ({ label, value: extras[label] }));
  }

  /**
   * Whether this customer came from an ERP customer import.
   *
   * Judged by the map having something in it rather than by a separate "is ERP" flag: a customer
   * with no extra fields has nothing for the tab to show, and `erpSyncHash` cannot serve — it is
   * deliberately cleared by every non-import write, so it would come and go as the customer is
   * edited.
   */
  hasErpFields(): boolean {
    return this.erpFields().length > 0;
  }
  @ViewChild(CustomerSettingsFormComponent, { static: false }) settingsFormComponent?: CustomerSettingsFormComponent;

  @Output() onSaveSuccess = new EventEmitter<void>();
  @Output() onCancel = new EventEmitter<void>();

  errorMessages = {
    contactFirstName: {
      required: 'First name is required',
      minlength: 'First name must be at least 2 characters',
      maxlength: 'First name cannot exceed 40 characters',
      pattern: 'First name can only contain letters and spaces',
    },
    contactLastName: {
      required: 'Last name is required',
      minlength: 'Last name must be at least 2 characters',
      maxlength: 'Last name cannot exceed 40 characters',
      pattern: 'Last name can only contain letters and spaces',
    },
    companyName: {
      required: 'Customer name is required',
      minlength: 'Customer name must be at least 2 characters',
      maxlength: 'Customer name cannot exceed 40 characters',
    },
    displayName: {
      required: 'Display name is required',
    },
    companyAccountNumber: {
      required: 'Account number is required',
      pattern: 'Account number can only contain letters, numbers, and hyphens',
    },
    businessPhone: {
      pattern: 'Invalid phone number',
    },
    businessPhoneExt: {
      pattern: 'Extension can only contain up to 10 digits',
    },
    mobilePhoneNumber: {
      pattern: 'Please enter a valid mobile number',
    },
    contactEmail: {
      //email: 'Please enter a valid email address',
      pattern: 'Please enter a valid email address',
    },
    notes: {
      maxlength: 'Notes cannot exceed 800 characters',
    },
    customerAddress: {
      maxlength: 'Address cannot exceed 255 characters',
    },
    customerCity: {
      maxlength: 'City cannot exceed 100 characters',
    },
    customerZip: {
      pattern: 'Please enter a valid ZIP code (12345 or 12345-6789)',
    },
  };

  usStates = [
    { code: 'AL', name: 'Alabama' },
    { code: 'AK', name: 'Alaska' },
    { code: 'AZ', name: 'Arizona' },
    { code: 'AR', name: 'Arkansas' },
    { code: 'CA', name: 'California' },
    { code: 'CO', name: 'Colorado' },
    { code: 'CT', name: 'Connecticut' },
    { code: 'DE', name: 'Delaware' },
    { code: 'FL', name: 'Florida' },
    { code: 'GA', name: 'Georgia' },
    { code: 'HI', name: 'Hawaii' },
    { code: 'ID', name: 'Idaho' },
    { code: 'IL', name: 'Illinois' },
    { code: 'IN', name: 'Indiana' },
    { code: 'IA', name: 'Iowa' },
    { code: 'KS', name: 'Kansas' },
    { code: 'KY', name: 'Kentucky' },
    { code: 'LA', name: 'Louisiana' },
    { code: 'ME', name: 'Maine' },
    { code: 'MD', name: 'Maryland' },
    { code: 'MA', name: 'Massachusetts' },
    { code: 'MI', name: 'Michigan' },
    { code: 'MN', name: 'Minnesota' },
    { code: 'MS', name: 'Mississippi' },
    { code: 'MO', name: 'Missouri' },
    { code: 'MT', name: 'Montana' },
    { code: 'NE', name: 'Nebraska' },
    { code: 'NV', name: 'Nevada' },
    { code: 'NH', name: 'New Hampshire' },
    { code: 'NJ', name: 'New Jersey' },
    { code: 'NM', name: 'New Mexico' },
    { code: 'NY', name: 'New York' },
    { code: 'NC', name: 'North Carolina' },
    { code: 'ND', name: 'North Dakota' },
    { code: 'OH', name: 'Ohio' },
    { code: 'OK', name: 'Oklahoma' },
    { code: 'OR', name: 'Oregon' },
    { code: 'PA', name: 'Pennsylvania' },
    { code: 'RI', name: 'Rhode Island' },
    { code: 'SC', name: 'South Carolina' },
    { code: 'SD', name: 'South Dakota' },
    { code: 'TN', name: 'Tennessee' },
    { code: 'TX', name: 'Texas' },
    { code: 'UT', name: 'Utah' },
    { code: 'VT', name: 'Vermont' },
    { code: 'VA', name: 'Virginia' },
    { code: 'WA', name: 'Washington' },
    { code: 'WV', name: 'West Virginia' },
    { code: 'WI', name: 'Wisconsin' },
    { code: 'WY', name: 'Wyoming' },
  ];

  availableParentCompanies: Array<{ id: string; displayName: string; companyAccountNumber: string }> = [];

  /**
   * The profile's active payment terms, for the picker.
   *
   * Always includes the customer's own term even when an operator has since
   * switched it off — otherwise opening the editor would show a blank control
   * and saving would silently clear a term the ERP set.
   */
  paymentTermOptions = signal<
    Array<{ value: string; description: string | null }>
  >([]);

  form: CustomerDetailsFormType = new FormGroup({
    contactFirstName: new FormControl('', [
      //Validators.required,
      Validators.minLength(2),
      Validators.maxLength(40),
      Validators.pattern(/^[A-Za-z\s]+$/),
    ]),
    contactLastName: new FormControl('', [
      // Validators.required,
      Validators.minLength(2),
      Validators.maxLength(40),
      Validators.pattern(/^[A-Za-z\s]+$/),
    ]),
    displayName: new FormControl('', [Validators.required]),
    companyName: new FormControl('', [Validators.required, Validators.minLength(2), Validators.maxLength(40)]),
    companyAccountNumber: new FormControl('', [Validators.required, Validators.pattern(/^[A-Za-z0-9-]+$/)]),
    businessPhone: new FormControl('', [Validators.pattern(/^\(\d{3}\)\s\d{3}-\d{4}$/)]),
    businessPhoneExt: new FormControl('', [Validators.pattern(/^\d{0,10}$/)]),
    mobilePhoneNumber: new FormControl('', [Validators.pattern(/^\(\d{3}\)\s\d{3}-\d{4}$/)]),
    // Holds the canonical `;`-delimited string; individual addresses are validated
    // by the chip UI as they are added (see addEmail).
    contactEmail: new FormControl(''),
    notes: new FormControl('', [Validators.maxLength(800)]),
    customerAddress: new FormControl('', [Validators.maxLength(255)]),
    customerCity: new FormControl('', [Validators.maxLength(100)]),
    customerState: new FormControl(''),
    customerZip: new FormControl('', [Validators.pattern(/^\d{5}(-\d{4})?$/)]),
    parentCustomerId: new FormControl(''),
    salesPersonId: new FormControl(''),
    paymentTerms: new FormControl(''),
    isTestCustomer: new FormControl(false),
  });

  /** Chip list of the customer's email addresses (UI representation of contactEmail). */
  contactEmails: string[] = [];
  emailError: string | null = null;

  private readonly userStore = inject(UserStore);
  private readonly dialog = inject(MatDialog);

  constructor(
    protected injector: Injector,
    protected customerService: CustomersService,
    protected actionsService: ActionsService,
    protected destroyRef: DestroyRef
  ) {}

  /** Add the typed address as a chip after validating and de-duplicating it. */
  addEmail(input: HTMLInputElement): void {
    const email = input.value.trim();
    this.emailError = null;
    if (!email) return;

    if (!EMAIL_PATTERN.test(email)) {
      this.emailError = this.errorMessages.contactEmail.pattern;
      return;
    }
    if (this.contactEmails.some((e) => e.toLowerCase() === email.toLowerCase())) {
      this.emailError = 'This email has already been added';
      return;
    }

    this.contactEmails.push(email);
    input.value = '';
    this.syncContactEmail();
  }

  removeEmail(index: number): void {
    this.contactEmails.splice(index, 1);
    this.syncContactEmail();
  }

  /** Push the chip list back into the form control as a `;`-delimited string. */
  private syncContactEmail(): void {
    this.form.controls.contactEmail.setValue(joinEmails(this.contactEmails));
    this.form.controls.contactEmail.markAsDirty();
  }

  onCompanyNameBlur() {
    const companyName = this.form.get('companyName')?.value || '';
    const displayName = this.form.get('displayName')?.value || '';

    if (companyName && !displayName) {
      this.form.patchValue({ displayName: companyName });
    }
  }

  /**
   * Load the profile's active payment terms for the picker.
   *
   * The customer's own term is added even when it is not in the active list, so
   * a term an operator has since switched off still displays instead of the
   * control opening blank and quietly clearing it on save. Failure leaves the
   * list empty, and the template falls back to a text box.
   */
  private loadPaymentTermOptions(currentTerm?: string | null): void {
    const profileId = this.userStore.activeProfile()?.profileDetail?.profileId;
    if (!profileId) return;

    this.customerService.getPaymentTermOptions(profileId).subscribe({
      next: (terms) => {
        const options: Array<{ value: string; description: string | null }> =
          terms.map((t) => ({ value: t.value, description: t.description }));
        const term = (currentTerm ?? '').trim();
        if (
          term &&
          !options.some((o) => o.value.toLowerCase() === term.toLowerCase())
        ) {
          options.push({ value: term, description: `${term} (inactive)` });
        }
        this.paymentTermOptions.set(options);
      },
      // The service already swallows a read failure into an empty list; this is
      // only here so an unexpected throw cannot break opening the editor.
      error: () => this.paymentTermOptions.set([]),
    });
  }

  ngAfterViewInit(): void {
    // Initialization logic after view is fully initialized
    if (this.customerData) {
      const customerData = this.customerData;
      this.customerId = customerData.id;
      this.contactEmails = splitEmails(customerData.contactEmail);

      let businessPhone = '';
      let businessPhoneExt = '';
      if (customerData.contactPhoneNumber) {
        [businessPhone, businessPhoneExt] = this.formatBusinessPhoneNumber(customerData.contactPhoneNumber);
      }

      this.form.patchValue({
        contactFirstName: customerData.contactFirstName || '',
        contactLastName: customerData.contactLastName || '',
        displayName: customerData.displayName || '',
        companyName: customerData.companyName || '',
        companyAccountNumber: customerData.companyAccountNumber || '',
        businessPhone: businessPhone,
        businessPhoneExt: businessPhoneExt,
        mobilePhoneNumber: customerData.mobilePhoneNumber || '',
        contactEmail: customerData.contactEmail || '',
        notes: customerData.notes || '',
        customerAddress: customerData.customerAddress || '',
        customerCity: customerData.customerCity || '',
        customerState: customerData.customerState || '',
        customerZip: customerData.customerZip || '',
        parentCustomerId: customerData.parentCustomerId || '',
        salesPersonId: customerData.salesPersonId || '',
        paymentTerms: customerData.paymentTerms || '',
        isTestCustomer: customerData.isTestCustomer ?? false,
      });
      this.markHydrated();
      this.loadPaymentTermOptions(customerData.paymentTerms);

      // Load available parent companies
      this.loadParentCompanies();
    } else if (this.customerId) {
      // Fetch customer data if only customerId is provided
      this.customerService
        .getCustomer(this.customerId)
        .pipe()
        .subscribe({
          next: (customer) => {
            let businessPhone = '';
            let businessPhoneExt = '';
            this.customerData = customer;
            this.contactEmails = splitEmails(customer.contactEmail);
            if (customer.contactPhoneNumber) {
              [businessPhone, businessPhoneExt] = this.formatBusinessPhoneNumber(customer.contactPhoneNumber);
            }

            this.form.patchValue({
              contactFirstName: customer.contactFirstName || '',
              contactLastName: customer.contactLastName || '',
              displayName: customer.displayName || '',
              companyName: customer.companyName || '',
              companyAccountNumber: customer.companyAccountNumber || '',
              businessPhone: businessPhone,
              businessPhoneExt: businessPhoneExt,
              mobilePhoneNumber: customer.mobilePhoneNumber || '',
              contactEmail: customer.contactEmail || '',
              notes: customer.notes || '',
              customerAddress: customer.customerAddress || '',
              customerCity: customer.customerCity || '',
              customerState: customer.customerState || '',
              customerZip: customer.customerZip || '',
              parentCustomerId: customer.parentCustomerId || '',
              salesPersonId: customer.salesPersonId || '',
              paymentTerms: customer.paymentTerms || '',
              isTestCustomer: customer.isTestCustomer ?? false,
            });
            this.markHydrated();

            // Load available parent companies
            this.loadParentCompanies();
            this.loadPaymentTermOptions(customer.paymentTerms);
          },
          error: (error) => {
            console.error('Error fetching customer data:', error);
          },
        });
    }
  }

  ngOnDestroy(): void {
    // Cleanup if necessary
  }

  ngOnChanges(changes: SimpleChanges): void {}

  formatBusinessPhoneNumber(phoneNumber: string): [string, string] {
    //console.log('Original contactPhoneNumber:', phoneNumber);

    // Remove any non-digit characters
    const digitsOnly = phoneNumber.replace(/\D/g, '');
    //console.log('Digits only:', digitsOnly);

    // Format phone number to match mask format (XXX) XXX-XXXX
    const formattedPhone = digitsOnly.slice(0, 10).replace(/^(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
    // Get any remaining digits as extension
    const formattedPhoneExt = digitsOnly.slice(10);

    //console.log('Formatted phone:', formattedPhone);
    //console.log('Split results:', {
    //  phoneNumber: formattedPhone,
    //  extension: formattedPhoneExt
    //});

    return [formattedPhone, formattedPhoneExt];
  }

  /**
   * Whether the operator has edited anything since the customer was loaded or last saved.
   *
   * Both forms count: the settings toggles live in their own child form, and an edit there is as
   * much an unsaved change as one to the name. `patchValue` on load does not dirty a control, so a
   * freshly opened editor reads clean.
   */
  hasUnsavedChanges(): boolean {
    return this.form.dirty || !!this.settingsFormComponent?.form?.dirty;
  }

  /**
   * Loading the customer is not an edit, so the form starts clean.
   *
   * `patchValue` alone does not guarantee that. ngx-mask (the two phone inputs) answers a write of
   * an EMPTY value by calling `onChange('')` from inside `writeValue`, and Angular takes any
   * `onChange` as input from the view and marks the control dirty. So a customer with no business
   * or mobile phone (most of them) opened the editor already "changed", and leaving the tab
   * asked the operator to save or discard edits they never made.
   */
  private markHydrated(): void {
    this.form.markAsPristine();
  }

  private markSaved(): void {
    this.form.markAsPristine();
    this.settingsFormComponent?.form?.markAsPristine();
  }

  /**
   * Called before anything takes the operator away from the editor (another tab, closing the
   * sheet, leaving the page). Resolves true when it is safe to leave.
   *
   * With unsaved edits the operator must say what happens to them — save or discard — or stay.
   * A save that fails (invalid form, API error) keeps them here, since leaving would lose the edits
   * they just asked to keep.
   */
  async confirmLeave(): Promise<boolean> {
    if (!this.hasUnsavedChanges()) return true;

    const choice = await openUnsavedChangesDialog(this.dialog, {
      message: 'You have changes to this customer that have not been saved. Save them or discard them before leaving.',
    });
    if (choice === 'save') return this.saveCustomer();
    if (choice === 'discard') {
      this.markSaved();
      return true;
    }
    return false;
  }

  /** The browser's own prompt for a refresh, a closed tab or an address typed over the page. */
  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.hasUnsavedChanges()) {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  /** Resolves true when the customer was saved, false when validation or the API refused it. */
  saveCustomer(): Promise<boolean> {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return Promise.resolve(false);
    }
    const formData = this.form.getRawValue();

    // Get customer settings from the settings form component
    const customerSettings = this.settingsFormComponent?.getFormValue();

    console.log('[CustomerEditor] Saving customer with settings:', customerSettings);

    const customer: CustomerModel = {
      id: this.customerId,
      contactFirstName: formData.contactFirstName || undefined,
      contactLastName: formData.contactLastName || undefined,
      displayName: formData.displayName || undefined,
      companyName: formData.companyName || undefined,
      companyAccountNumber: formData.companyAccountNumber || undefined,
      contactPhoneNumber: formData.businessPhone
        ? formData.businessPhone + (formData.businessPhoneExt ? ' ' + formData.businessPhoneExt : '')
        : undefined,
      mobilePhoneNumber: formData.mobilePhoneNumber || undefined,
      contactEmail: formData.contactEmail || undefined,
      notes: formData.notes || undefined,
      customerSettings: customerSettings,
      customerAddress: formData.customerAddress || undefined,
      customerCity: formData.customerCity || undefined,
      customerState: formData.customerState || undefined,
      customerZip: formData.customerZip || undefined,
      parentCustomerId: formData.parentCustomerId || undefined,
      salesPersonId: formData.salesPersonId || null,
      paymentTerms: formData.paymentTerms || null,
      isTestCustomer: formData.isTestCustomer ?? false,
      // ERP-owned, and the form has no control for it. Carried through from the
      // loaded record because the API coerces an absent `inactive` to false —
      // without this, saving an ERP-retired customer silently un-retires it.
      inactive: this.customerData?.inactive,
      inactiveDate: this.customerData?.inactiveDate,
    };

    console.log('[CustomerEditor] Customer data to save:', customer);

    return new Promise<boolean>((resolve) => {
      this.actionsService.handleCustomerSaveAction(
        customer,
        formData,
        () => {
          if (this.customerData) {
            this.customerData = { ...this.customerData, ...customer };
          } else {
            this.customerData = customer;
          }
          // Clean before emitting: the host reacts by leaving the editor, and must not find
          // the changes it just saved still reading as unsaved.
          this.markSaved();
          // Emit success event to close the editor
          this.onSaveSuccess.emit();
          resolve(true);
        },
        () => resolve(false)
      );
    });

    // const form = customerFormComponent!.form;
    //         if (!form.valid) {
    //           form.markAllAsTouched();
    //           return;
    //         }
    //         const formData = form.getRawValue();

    //         actionsService.handleCustomerSaveAction(
    //           customer,
    //           formData,
    //           () => {
    //             modal.close();
    //             if (component && (component as any).parent && (component as any).parent.parent) {
    //               (component as any).parent.parent.$allowNext = true;
    //             }
    //           }
    //         );
  }
  cancelEdit() {
    // Cancel IS the operator discarding their edits, so it does not ask again.
    this.markSaved();
    // Emit cancel event to close the editor without saving
    this.onCancel.emit();
  }

  loadParentCompanies() {
    // Get all customers for the current profile to populate parent company dropdown
    if (this.customerData?.freelyProfileId) {
      this.customerService
        .getCustomers()
        .pipe()
        .subscribe({
          next: (customers: CustomerModel[]) => {
            this.availableParentCompanies = customers
              .filter((c) => c.id !== this.customerId) // Exclude current customer
              .map((c) => ({
                id: c.id!,
                displayName: c.displayName || c.companyName || '',
                companyAccountNumber: c.companyAccountNumber || '',
              }))
              .sort((a, b) => a.displayName.localeCompare(b.displayName));
          },
          error: (error) => {
            console.error('Error loading parent companies:', error);
          },
        });
    }
  }
}
